interface ElementRadarChartProps {
  balance: {
    wood: number;
    fire: number;
    earth: number;
    metal: number;
    water: number;
  };
}

export default function ElementRadarChart({ balance }: ElementRadarChartProps) {
  const elements = [
    { name: '木', nameKo: '목', value: balance.wood, color: '#10b981', glowColor: 'rgba(16, 185, 129, 0.5)', angle: 0, icon: 'ri-plant-line' },
    { name: '火', nameKo: '화', value: balance.fire, color: '#ef4444', glowColor: 'rgba(239, 68, 68, 0.5)', angle: 72, icon: 'ri-fire-line' },
    { name: '土', nameKo: '토', value: balance.earth, color: '#f59e0b', glowColor: 'rgba(245, 158, 11, 0.5)', angle: 144, icon: 'ri-earth-line' },
    { name: '金', nameKo: '금', value: balance.metal, color: '#9ca3af', glowColor: 'rgba(156, 163, 175, 0.5)', angle: 216, icon: 'ri-copper-diamond-line' },
    { name: '水', nameKo: '수', value: balance.water, color: '#3b82f6', glowColor: 'rgba(59, 130, 246, 0.5)', angle: 288, icon: 'ri-water-flash-line' }
  ];

  const maxValue = 8;
  const centerX = 175;
  const centerY = 175;
  const maxRadius = 120;

  // 육각형 배경 그리드 생성
  const createPolygonPoints = (radius: number) => {
    return elements
      .map((el) => {
        const angle = (el.angle - 90) * (Math.PI / 180);
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        return `${x},${y}`;
      })
      .join(' ');
  };

  // 데이터 포인트 생성
  const dataPoints = elements
    .map((el) => {
      const angle = (el.angle - 90) * (Math.PI / 180);
      const radius = (el.value / maxValue) * maxRadius;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className="flex flex-col items-center">
      <svg width="350" height="350" className="mb-8 drop-shadow-2xl">
        {/* 배경 그리드 - 더 선명하게 */}
        {[0.25, 0.5, 0.75, 1].map((scale, i) => (
          <polygon
            key={i}
            points={createPolygonPoints(maxRadius * scale)}
            fill="none"
            stroke={i === 3 ? 'rgba(251, 191, 36, 0.3)' : 'rgba(255, 255, 255, 0.15)'}
            strokeWidth={i === 3 ? '2' : '1'}
            strokeDasharray={i === 3 ? '0' : '4 4'}
          />
        ))}

        {/* 축선 - 더 밝게 */}
        {elements.map((el, i) => {
          const angle = (el.angle - 90) * (Math.PI / 180);
          const x = centerX + maxRadius * Math.cos(angle);
          const y = centerY + maxRadius * Math.sin(angle);
          return (
            <line
              key={i}
              x1={centerX}
              y1={centerY}
              x2={x}
              y2={y}
              stroke="rgba(255, 255, 255, 0.2)"
              strokeWidth="1.5"
            />
          );
        })}

        {/* 데이터 영역 - 그라디언트 강화 */}
        <defs>
          <radialGradient id="radarGradient">
            <stop offset="0%" stopColor="rgba(251, 191, 36, 0.6)" />
            <stop offset="50%" stopColor="rgba(168, 85, 247, 0.4)" />
            <stop offset="100%" stopColor="rgba(59, 130, 246, 0.2)" />
          </radialGradient>
          
          {/* 각 요소별 그림자 효과 */}
          {elements.map((el, i) => (
            <filter key={i} id={`glow-${i}`}>
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          ))}
        </defs>

        <polygon
          points={dataPoints}
          fill="url(#radarGradient)"
          stroke="rgba(251, 191, 36, 0.8)"
          strokeWidth="3"
          strokeLinejoin="round"
        />

        {/* 데이터 포인트 - 더 크고 화려하게 */}
        {elements.map((el, i) => {
          const angle = (el.angle - 90) * (Math.PI / 180);
          const radius = (el.value / maxValue) * maxRadius;
          const x = centerX + radius * Math.cos(angle);
          const y = centerY + radius * Math.sin(angle);
          return (
            <g key={i}>
              {/* 외부 글로우 */}
              <circle
                cx={x}
                cy={y}
                r="10"
                fill={el.glowColor}
                opacity="0.6"
              />
              {/* 메인 포인트 */}
              <circle
                cx={x}
                cy={y}
                r="6"
                fill={el.color}
                stroke="white"
                strokeWidth="3"
                filter={`url(#glow-${i})`}
              />
            </g>
          );
        })}

        {/* 중앙 장식 */}
        <circle
          cx={centerX}
          cy={centerY}
          r="8"
          fill="rgba(251, 191, 36, 0.3)"
          stroke="rgba(251, 191, 36, 0.8)"
          strokeWidth="2"
        />

        {/* 레이블 - 더 크고 선명하게 */}
        {elements.map((el, i) => {
          const angle = (el.angle - 90) * (Math.PI / 180);
          const labelRadius = maxRadius + 35;
          const x = centerX + labelRadius * Math.cos(angle);
          const y = centerY + labelRadius * Math.sin(angle);
          return (
            <g key={i}>
              {/* 배경 원 */}
              <circle
                cx={x}
                cy={y}
                r="28"
                fill={el.glowColor}
                opacity="0.3"
              />
              {/* 한자 */}
              <text
                x={x}
                y={y - 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize="24"
                fontWeight="bold"
                style={{ textShadow: `0 0 10px ${el.glowColor}` }}
              >
                {el.name}
              </text>
              {/* 한글 */}
              <text
                x={x}
                y={y + 18}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="rgba(255, 255, 255, 0.7)"
                fontSize="13"
                fontWeight="600"
              >
                {el.nameKo}
              </text>
            </g>
          );
        })}
      </svg>

      {/* 범례 - 아이콘과 함께 */}
      <div className="grid grid-cols-5 gap-4 w-full max-w-lg">
        {elements.map((el, i) => (
          <div key={i} className="text-center group">
            <div className="relative mb-3">
              {/* 글로우 효과 */}
              <div
                className="absolute inset-0 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity"
                style={{ backgroundColor: el.color }}
              ></div>
              {/* 메인 원 */}
              <div
                className="relative w-12 h-12 rounded-full mx-auto flex items-center justify-center border-2 border-white/30 group-hover:scale-110 transition-transform shadow-lg"
                style={{ backgroundColor: el.color }}
              >
                <i className={`${el.icon} text-xl text-white`}></i>
              </div>
            </div>
            <p className="text-white text-base font-bold mb-1">{el.value}</p>
            <p className="text-white/60 text-xs">{el.nameKo}</p>
          </div>
        ))}
      </div>
    </div>
  );
}